import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { ReportListItemApiModel } from './model/report-list-item-api.model';
import { ReportApiModel } from './model/report-api.model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';

@Injectable()
export class ReportsApi {

    constructor(
        private neptuneApi: NeptuneApi
    ) {
    }

    /**
     * Gets a list of limited report information
     */
    public getList(): Observable<ReportListItemApiModel[]> {
        return this.neptuneApi.api<ReportListItemApiModel[]>(
            '/reports',
            null,
            HttpVerbsEnum.GET,
            {},
            null,
            null,
            'v2'
        );
    }

    /**
     * Gets the full details for a specific ID
     */
    public get(id: number): Observable<ReportApiModel> {
        return this.neptuneApi.api<ReportApiModel>(
            `/reports/${id}`,
            null,
            HttpVerbsEnum.GET,
            {},
            null,
            null,
            'v2'
        );
    }

    /**
     * Helper method: If the report has an ID, call update, else, create
     */
    public save(report: ReportApiModel): Observable<number> {
        if (report.id) {
            return this.update(report);
        } else {
            return this.create(report);
        }
    }

    /**
     * Updates and existing report
     */
    public update(report: ReportApiModel): Observable<number> {
        return this.neptuneApi.api<number>(
            `/reports/${report.id}`,
            report,
            HttpVerbsEnum.PUT,
            {},
            null,
            null,
            'v2'
        );
    }

    /**
     * Creates a new report
     */
    public create(report: ReportApiModel): Observable<number> {
        return this.neptuneApi.api<number>(
            '/reports',
            report,
            HttpVerbsEnum.POST,
            {},
            null,
            null,
            'v2'
        );
    }

    /**
     * Deletes the report specified by the ID
     */
    public delete(id: number): Observable<null> {
        return this.neptuneApi.api<null>(
            `/reports/${id}`,
            null,
            HttpVerbsEnum.DELETE,
            {},
            null,
            null,
            'v2'
        );
    }

    /**
     * Adds and/or removes reports from provided group
     * @param addIds Reports to add to group
     * @param removeIds Reports to remove from group
     * @param groupId
     * @returns
     */
    public updateReportDisplayGroup(addIds: number[], removeIds: number[], groupId: number) {
        const slug: string = `/reports/displaygroups/${groupId}`;
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH,
            {},
            null,
            null,
            'v2'
        );
    }

    /**
     * Removes group ID from all provided reports
     * @param removeIds reports to remove from groups
     * @returns
     */
    public removeReportsFromDisplayGroup(removeIds: number[]) {
        const slug: string = '/reports/displaygroups';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH,
            {},
            null,
            null,
            'v2'
        );
    }

    public updateReportsFavorites(addIds: number[], removeIds: number[]) {
        const slug: string = '/reports/displaygroups/favorites';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH,
            {},
            null,
            null,
            'v2'
        );
    }
}
